import { USER_TIERS } from '../config/entitlements';
import goblinDocument from '../data/monsters/goblin.json';
import hobgoblinDocument from '../data/monsters/hobgoblin.json';
import wolfDocument from '../data/monsters/wolf.json';

export const BATTLE_ROOM_TEST_ROUTE = '/__test__/battle-room';
export const BATTLE_ROOM_TEST_SESSION_ID = 'battle-room-harness-session';
export const BATTLE_ROOM_TEST_CHARACTER_ID = 'battle_test_hero';
export const BATTLE_ROOM_TEST_MONSTER_ID = 'battle_test_goblin';
export const BATTLE_ROOM_TEST_HOBGOBLIN_ID = 'battle_test_hobgoblin';
export const BATTLE_ROOM_TEST_WOLF_ID = 'battle_test_wolf';
export const BATTLE_ROOM_TEST_CAMPAIGN_ID = 'battle-test-campaign';
export const ENTITLEMENTS_CACHE_KEY = 'vcs_entitlements_snapshot';
export const UNIFIED_ENTITY_STORAGE_KEY = 'ascendant_unified_entities';

export const battleRoomHarnessCharacterEntity = {
  id: BATTLE_ROOM_TEST_CHARACTER_ID,
  type: 'character',
  schemaVersion: '1.0',
  data: {
    name: 'Battle Test Hero',
    identity: {
      name: 'Battle Test Hero',
      race: 'Human',
      background: 'Soldier',
      alignment: 'Lawful Good'
    },
    class: 'Fighter',
    level: 5,
    classes: [
      { name: 'Fighter', level: 5 }
    ],
    hp: {
      average: 38,
      formula: '5d10+10'
    },
    ac: {
      value: 17
    },
    speed: {
      walk: 30
    },
    abilities: {
      strength: 16,
      dexterity: 12,
      constitution: 14,
      intelligence: 10,
      wisdom: 11,
      charisma: 13
    },
    savingThrows: {
      strength: 6,
      constitution: 5
    },
    skills: {
      athletics: 6,
      perception: 3
    },
    equipment: [
      { name: 'Longsword', type: 'weapon', equipped: true, attackBonus: 6, damage: '1d8+3' },
      { name: 'Shield', type: 'shield', equipped: true }
    ],
    actions: [
      { name: 'Longsword', attackBonus: 6, damage: '1d8+3', damageType: 'slashing' }
    ],
    spells: []
  },
  metadata: {
    createdAt: '2026-04-13T00:00:00.000Z',
    updatedAt: '2026-04-13T00:00:00.000Z'
  },
  tags: ['test-harness']
};

export const battleRoomHarnessMonsterEntity = {
  ...goblinDocument,
  id: BATTLE_ROOM_TEST_MONSTER_ID,
  data: {
    ...goblinDocument.data,
    name: 'Battle Test Goblin'
  },
  // Exercise the same versioned source used by production instead of a
  // parallel hand-written creature shape. date_of_change: 2026-09-07.
  tags: [...(goblinDocument.tags || []), 'test-harness']
};

// A real versioned default is included for mechanics-control journeys; only
// its display identity changes for deterministic harness lookup. date_of_change: 2026-09-06
export const battleRoomHarnessHobgoblinEntity = {
  ...hobgoblinDocument,
  id: BATTLE_ROOM_TEST_HOBGOBLIN_ID,
  data: {
    ...hobgoblinDocument.data,
    name: 'Battle Test Hobgoblin'
  },
  tags: [...(hobgoblinDocument.tags || []), 'test-harness']
};

export const battleRoomHarnessWolfEntity = {
  ...wolfDocument,
  id: BATTLE_ROOM_TEST_WOLF_ID,
  data: {
    ...wolfDocument.data,
    name: 'Battle Test Wolf'
  },
  tags: [...(Array.isArray(wolfDocument.tags) ? wolfDocument.tags : []), 'test-harness']
};

const campaignMonsterData = (name) => ({
  name,
  type: 'monster',
  entityKind: 'npc',
  hp: 18,
  maxHp: 18,
  ac: 13,
  speed: 30,
  abilities: {
    strength: 12,
    dexterity: 14,
    constitution: 12,
    intelligence: 10,
    wisdom: 11,
    charisma: 9
  },
  actions: [
    { name: 'Spear', attackBonus: 4, damage: '1d6+2', damageType: 'piercing' }
  ]
});

export const battleRoomHarnessCampaignMonsterEntity = {
  id: 'battle_test_campaign_guard',
  type: 'monster',
  schemaVersion: '1.0',
  data: {
    ...campaignMonsterData('Battle Test Campaign Guard'),
    externalSource: {
      system: 'gamemastercraft',
      campaignId: BATTLE_ROOM_TEST_CAMPAIGN_ID,
      entityId: 'gmc-campaign-guard'
    },
    _meta: { sourceSnapshot: campaignMonsterData('Battle Test Campaign Guard') }
  },
  tags: ['test-harness', 'campaign']
};

export const battleRoomHarnessOtherCampaignMonsterEntity = {
  id: 'battle_test_other_campaign_guard',
  type: 'monster',
  schemaVersion: '1.0',
  data: {
    ...campaignMonsterData('Battle Test Other Campaign Guard'),
    externalSource: {
      system: 'gamemastercraft',
      campaignId: 'other-campaign',
      entityId: 'gmc-other-campaign-guard'
    },
    _meta: { sourceSnapshot: campaignMonsterData('Battle Test Other Campaign Guard') }
  },
  tags: ['test-harness', 'campaign']
};

export const battleRoomHarnessIncompleteCampaignMonsterEntity = {
  id: 'battle_test_incomplete_campaign_actor',
  type: 'monster',
  schemaVersion: '1.0',
  data: {
    ...campaignMonsterData('Battle Test Incomplete Campaign Actor'),
    externalSource: {
      system: 'gamemastercraft',
      campaignId: BATTLE_ROOM_TEST_CAMPAIGN_ID,
      entityId: 'gmc-incomplete-campaign-actor'
    },
    // Projection defaults make the editor usable, while the source snapshot
    // proves the GMC authoring record is not combat ready. date_of_change: 2026-08-01
    _meta: { sourceSnapshot: { name: 'Battle Test Incomplete Campaign Actor', actions: [] } }
  },
  tags: ['test-harness', 'campaign', 'incomplete']
};

export function isBattleRoomHarnessPath(pathname) {
  return pathname === BATTLE_ROOM_TEST_ROUTE || pathname.startsWith(`${BATTLE_ROOM_TEST_ROUTE}/`);
}

export function buildBattleRoomHarnessStorage() {
  return {
    characters: {
      [battleRoomHarnessCharacterEntity.id]: battleRoomHarnessCharacterEntity
    },
    monsters: {
      [battleRoomHarnessMonsterEntity.id]: battleRoomHarnessMonsterEntity,
      [battleRoomHarnessHobgoblinEntity.id]: battleRoomHarnessHobgoblinEntity,
      [battleRoomHarnessWolfEntity.id]: battleRoomHarnessWolfEntity,
      [battleRoomHarnessCampaignMonsterEntity.id]: battleRoomHarnessCampaignMonsterEntity,
      [battleRoomHarnessOtherCampaignMonsterEntity.id]: battleRoomHarnessOtherCampaignMonsterEntity,
      [battleRoomHarnessIncompleteCampaignMonsterEntity.id]: battleRoomHarnessIncompleteCampaignMonsterEntity
    },
    version: '1.0'
  };
}

export function buildBattleRoomHarnessEntitlementsSnapshot(tier = USER_TIERS.PREMIUM) {
  return {
    userTier: tier,
    limits: null,
    source: 'battle-room-harness',
    fetchedAt: Date.now()
  };
}

export function buildBattleRoomHarnessParticipants() {
  return [
    {
      userId: 'harness-gm',
      userName: 'Harness GM',
      role: 'gm'
    },
    {
      userId: 'harness-player',
      userName: 'Harness Player',
      role: 'player'
    }
  ];
}
